import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { AutoComplete } from 'primeng/autocomplete';

/**
 * Type-to-filter dropdown with an inline "Add new" action.
 * Wraps PrimeNG 15 p-autoComplete so the v20 AutoComplete API change stays in one file.
 * The form value is the option value (or array of values when `multiple`), not the option object.
 */
@Component({
  selector: 'app-acan-select',
  templateUrl: './acan-select.component.html',
  styleUrls: ['./acan-select.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AcanSelectComponent),
      multi: true,
    },
  ],
})
export class AcanSelectComponent implements ControlValueAccessor, OnChanges {
  @Input() options: any[] = [];
  @Input() optionLabel = 'label';
  /** Set to null to use the whole option object as the value. */
  @Input() optionValue: string | null = 'value';
  @Input() multiple = false;
  @Input() placeholder = 'Select…';
  @Input() addNewLabel = 'Add new';
  @Input() allowAdd = false;
  @Input() itemTemplate: TemplateRef<any> | null = null;
  @Input() filterFn: ((option: any, query: string) => boolean) | null = null;
  @Input() appendTo: any = 'body';
  @Input() scrollHeight = '240px';
  @Input() inputId = '';

  @Output() addNew = new EventEmitter<string>();
  @Output() selectionChange = new EventEmitter<any>();

  @ViewChild(AutoComplete) autoComplete?: AutoComplete;

  selection: any = null;
  suggestions: any[] = [];
  lastQuery = '';
  disabled = false;

  private rawValue: any = null;
  private onChange: (v: any) => void = () => {};
  onTouched: () => void = () => {};

  constructor(private cd: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options']) {
      this.resolveSelection();
      if (this.autoComplete?.overlayVisible) {
        this.filter(this.lastQuery);
      }
    }
  }

  writeValue(value: any): void {
    this.rawValue = value;
    this.resolveSelection();
  }

  registerOnChange(fn: (v: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cd.markForCheck();
  }

  labelOf(option: any): string {
    if (option == null) return '';
    if (typeof option !== 'object') return String(option);
    const v = option[this.optionLabel];
    return v == null ? '' : String(v);
  }

  private valueOf(option: any): any {
    if (option == null || typeof option !== 'object') return option;
    return this.optionValue ? option[this.optionValue] : option;
  }

  private sameValue(a: any, b: any): boolean {
    if (a == null || b == null) return false;
    const ka = typeof a === 'object' ? a._id ?? a : a;
    const kb = typeof b === 'object' ? b._id ?? b : b;
    return String(ka) === String(kb);
  }

  private findOption(value: any): any {
    return (this.options || []).find(o => this.sameValue(this.valueOf(o), value));
  }

  private resolveSelection(): void {
    const v = this.rawValue;
    if (this.multiple) {
      const list = Array.isArray(v) ? v : [];
      this.selection = list
        .map(item => this.findOption(item) || (typeof item === 'object' ? item : null))
        .filter(Boolean);
    } else if (v == null || v === '') {
      this.selection = null;
    } else {
      this.selection = this.findOption(v) || (typeof v === 'object' ? v : null);
    }
    this.cd.markForCheck();
  }

  filter(query: string): void {
    this.lastQuery = (query || '').trim();
    const q = this.lastQuery.toLowerCase();
    const selected = this.multiple && Array.isArray(this.rawValue) ? this.rawValue : [];
    const opts = (this.options || []).filter(
      o => !selected.some((v: any) => this.sameValue(this.valueOf(o), v))
    );
    this.suggestions = q
      ? opts.filter(o =>
          this.filterFn ? this.filterFn(o, q) : this.labelOf(o).toLowerCase().includes(q)
        )
      : [...opts];
  }

  onSelectionChange(sel: any): void {
    if (this.multiple) {
      const list = Array.isArray(sel) ? sel : [];
      this.rawValue = list.map(o => this.valueOf(o));
    } else {
      this.rawValue = sel && typeof sel === 'object' ? this.valueOf(sel) : null;
    }
    this.onChange(this.rawValue);
    this.selectionChange.emit(this.rawValue);
  }

  /**
   * Open the panel when clicking the field (not only the chevron).
   * Use mousedown + blank-query dropdown path so an already-focused /
   * selected value still shows the full list.
   */
  onFieldMouseDown(event: MouseEvent): void {
    const ac = this.autoComplete;
    if (!ac || this.disabled || ac.overlayVisible) return;
    const target = event.target as HTMLElement | null;
    if (
      target?.closest(
        '.p-autocomplete-dropdown, .p-autocomplete-token-icon, .p-autocomplete-clear-icon'
      )
    ) {
      return;
    }
    if (
      !target?.closest(
        '.p-autocomplete-input, .p-inputtext, .p-autocomplete-multiple-container, .p-autocomplete-input-token'
      )
    ) {
      return;
    }
    // Same path as the chevron: search('') then show().
    ac.handleDropdownClick(event);
  }

  emitAdd(): void {
    const query = this.lastQuery;
    this.autoComplete?.hide();
    this.addNew.emit(query);
  }
}
